/*
 * Copyright Mimic Networks, Inc. 2026.
 */

export const en = {
  translation: {
    global: {
      hello: 'Hello',
      ops: 'Ops, something went wrong',
      dashboard: 'Dashboard',
      signIn: 'Sign In',
      signOut: 'Sign Out',
      copyright: 'Copyright',
      next: 'Next',
      back: 'Back',
      finish: 'Finish',
      copy: 'Copy',
      copied: 'Copied',
      failedCopy: 'Failed to copy to clipboard',
      example: 'Example',
      loading: 'Loading',
      regenerate: 'Regenerate',
      notAvailable: 'Not available',
      apiRequestFailedTitle: 'API Request failed',
      apiRequestFailedDescription: '{{method}} request to {{url}} failed: {{error}}',
      forms: {
        required: 'This is required',
        invalid: 'Invalid',
      },
    },
    assignConfigOverlay: {
      title: 'Select a configuration to assign to node',
      cancel: 'Cancel',
      assign: 'Assign',
    },
    apiTokens: {
      apiTokenCreated: 'API token created',
      confirmDelete: {
        title: 'Are you sure you want to revoke “{{name}}”?',
        body1:
          'Revoking this API token will immediately disable it, and it will no longer be usable for authenticating with the Mimic API.',
        body2: 'This action cannot be reversed.',
        cancel: 'No, cancel',
        ok: 'Yes, revoke',
      },
      creationError: 'Error creating API token',
      createTokenModal: {
        nameRequired: 'API token name is required',
        nameMaxLength: 'API token name max length is {{maxLength}} characters',
        namePlaceholder: 'API token name',
        permissions: 'Permissions',
        permissionsRequired: 'At least one permission is required',
        selectAtLeastOnePermission: 'Please select at least one permission',
        cancel: 'Cancel',
        create: 'Create',
        ok: 'Ok',
        title: 'Create a new API token',
      },
      listTitle: 'API Tokens',
      table: {
        apiTokenName: 'API Token Name',
        permissions: 'Permissions',
        createdBy: 'Created By',
        dateCreated: 'Date Created (UTC)',
        revoke: 'Revoke',
      },
      revocationError: 'Error revoking API token',
      tokenRevoked: 'API token revoked',
      copyTokenWarning: 'This is the only time you can copy this token.',
      copyTokenWarningNote: 'Note!',
      failedCopy: 'Failed to copy API token',
      tokenCopied: 'API token copied',
      newAPIToken: 'New API token',
      description:
        'API tokens allow you to perform actions against the Mimic API. They are useful for automation, scripts, or third-party integrations.',
      permissionsSection: {
        title: 'Permissions',
        subtitle: 'Choose which permissions this token should have',
        loading: 'Loading permissions...',
        resourceColumn: 'Resource',
      },
      tokenForm: {
        cancel: 'Cancel',
        generalInfo: 'General Information',
        nameLabel: 'Name',
        nameHelp: 'A name to help you identify this token',
        namePlaceholder: 'Enter token name',
      },
    },
    installerTokens: {
      listTitle: 'Installer Tokens',
      description: 'Manage tokens for secure deployment of the Mimic Node',
      generateToken: 'Generate token',
      viewRevokedTokens: 'View revoked tokens',
      revokedTokensTitle: 'Revoked tokens',
      revokedTokensDescription: 'View revoked installer tokens',
      backToInstallerTokens: 'Back to Installer Tokens',
      createModalTitle: 'Generate Installer Token',
      createModalDescription:
        'This token will be used to securely install the Mimic Node and provision it with the right configuration.',
      tokenNameLabel: 'Token name',
      tokenNameRequired: 'Token name is required',
      tokenNamePlaceholder: 'Enter name',
      tokenNameTooltip: 'Enter a unique name to keep track of your installer token',
      tokenNameMaxLength: 'Token name max length is {{maxLength}} characters',
      duplicateTokenName: 'This token name is already being used',
      fleetLabel: 'Fleet',
      fleetTooltip: 'Nodes installed with this token will be automatically assigned to this fleet when eligible',
      fleetPlaceholder: 'Select a fleet (optional)',
      fleetLoading: 'Loading fleets...',
      fleetNotFound: 'No fleets found',
      generate: 'Generate',
      successModalTitle: 'New Installer Token Created',
      successModalDescription:
        'Please copy and save this token ID somewhere safe and secure as it will only be available at this time.',
      tokenIdLabel: 'Installer Token ID',
      tokenNotVisibleWarning: 'This installer token ID will not be visible after closing this modal',
      copyAndClose: 'Copy and close',
      failedCopy: 'Failed to copy Installer token',
      creationError: 'Error creating installer token',
      tokenGeneratedAndCopied: 'New token generated and copied to clipboard',
      table: {
        jti: 'JTI',
        tokenName: 'Token Name',
        createdBy: 'Created By',
        dateCreated: 'Date Created (UTC)',
        timesUsed: 'Times Used',
        fleet: 'Fleet',
        revoke: 'Revoke',
        revokedBy: 'Revoked By',
        dateRevoked: 'Date Revoked (UTC)',
      },
      confirmDelete: {
        title: 'Revoke this token?',
        body1: 'Revoking this token will not impact existing nodes but it can no longer be used to install new nodes.',
        cancel: 'Cancel',
        ok: 'Revoke',
      },
      revocationError: 'Error revoking installer token',
      tokenRevoked: 'Installer token revoked',
    },
    oauthClients: {
      clientCreated: 'OAuth client created',
      clientUpdated: 'OAuth client updated',
      clientDeleted: 'OAuth client deleted',
      clientNotFound: 'OAuth client not found',
      confirmDelete: {
        title: 'Are you sure you want to delete "{{name}}"?',
        body1:
          'Deleting this OAuth client will immediately disable it, and it will no longer be usable for authentication.',
        body2: 'This action cannot be reversed.',
        cancel: 'No, cancel',
        ok: 'Yes, delete',
      },
      creationError: 'Error creating OAuth client',
      updateError: 'Error updating OAuth client',
      deleteError: 'Error deleting OAuth client',
      createClientModal: {
        title: 'Create a new OAuth client',
      },
      createdModal: {
        clientIdLabel: 'Client ID',
        clientSecretLabel: 'Client Secret',
        warning: 'This is the only time you can view the client secret. Save it in a secure location.',
        warningNote: 'Important!',
        ok: 'Ok',
      },
      listTitle: 'OAuth Clients',
      newOAuthClient: 'New OAuth client',
      table: {
        name: 'Name',
        clientId: 'Client ID',
        redirectUris: 'Redirect URIs',
        dateCreated: 'Date Created (UTC)',
        actions: 'Actions',
        view: 'View',
        delete: 'Delete',
        more: 'more',
      },
      failedCopy: 'Failed to copy',
      description:
        'OAuth clients allow third-party applications to authenticate users and access resources on their behalf. Configure redirect URIs and other settings to enable secure OAuth 2.0 flows.',
      clientForm: {
        cancel: 'Cancel',
        create: 'Create',
        generalInfo: 'General Information',
        nameLabel: 'Name',
        nameRequired: 'OAuth client name is required',
        nameMaxLength: 'OAuth client name max length is {{maxLength}} characters',
        nameHelp: 'A descriptive name to identify this OAuth client',
        namePlaceholder: 'Enter client name',
        redirectUrisTitle: 'Redirect URIs',
        redirectUrisLabel: 'Redirect URI',
        redirectUrisRequired: 'At least one redirect URI is required',
        redirectUriRequired: 'Redirect URI is required',
        redirectUriInvalid: 'Please enter a valid URL (http:// or https://)',
        redirectUrisHelp: 'URLs where users will be redirected after authentication. Add multiple URIs if needed.',
        redirectUriPlaceholder: 'https://example.com/callback',
        addRedirectUri: 'Add redirect URI',
        advancedSettings: 'Advanced Settings',
        signatureAlgLabel: 'ID Token Signature Algorithm',
        signatureAlgHelp: 'Algorithm used to sign ID tokens. Leave empty to use the default (RS256).',
        signatureAlgOnlyRS256: 'RS256 is the only supported algorithm currently.',
        signatureAlgPlaceholder: 'Select algorithm',
      },
      detailsPage: {
        basicInfo: 'Basic Information',
        configuration: 'Configuration',
        clientId: 'Client ID',
        name: 'Name',
        redirectUris: 'Redirect URIs',
        confidential: 'Confidential',
        pkce: 'PKCE',
        accessTokenTtl: 'Access Token TTL',
        authorizationCodeTtl: 'Authorization Code TTL',
        refreshTokenTtl: 'Refresh Token TTL',
        idTokenSignatureAlg: 'ID Token Signature Algorithm',
        supportedGrantTypes: 'Supported Grant Types',
        yes: 'Yes',
        no: 'No',
        enabled: 'Enabled',
        disabled: 'Disabled',
      },
      back: 'Back',
      backToList: 'Back to OAuth Clients',
      edit: 'Edit',
      delete: 'Delete',
      saveChanges: 'Save changes',
    },
    m2mClients: {
      listTitle: 'M2M Clients',
      newM2MClient: 'New M2M client',
      description:
        'M2M clients allow server applications to authenticate directly with the Mimic API using client credentials.',
      clientCreated: 'M2M client created',
      clientDeleted: 'M2M client deleted',
      creationError: 'Error creating M2M client',
      deleteError: 'Error deleting M2M client',
      failedCopy: 'Failed to copy',
      confirmDelete: {
        title: 'Are you sure you want to delete "{{name}}"?',
        body1:
          'Deleting this M2M client will immediately disable it, and it will no longer be usable for API authentication.',
        body2: 'This action cannot be reversed.',
        cancel: 'No, cancel',
        ok: 'Yes, delete',
      },
      table: {
        clientId: 'Client ID',
        name: 'Name',
        allowedScopes: 'Allowed Scopes',
        actions: 'Actions',
        view: 'View',
        delete: 'Delete',
        more: 'more',
      },
      backToList: 'Back to M2M Clients',
      delete: 'Delete',
      clientNotFound: 'M2M client not found',
      detailsPage: {
        basicInfo: 'Basic Information',
        clientId: 'Client ID',
        name: 'Name',
        allowedScopes: 'Allowed Scopes',
        configuration: 'Configuration',
        accessTokenTtl: 'Access Token TTL',
        supportedGrantTypes: 'Supported Grant Types',
      },
      createClient: {
        title: 'Create a new M2M client',
      },
      form: {
        generalInfo: 'General Information',
        nameLabel: 'Name',
        nameRequired: 'M2M client name is required',
        nameMaxLength: 'Name cannot exceed 70 characters',
        nameHelp: 'A descriptive name to identify this M2M client',
        namePlaceholder: 'Enter client name',
        scopesTitle: 'Allowed Scopes',
        scopesHelp: 'Scopes define what resources this client can access',
        scopesRequired: 'At least one scope is required',
        scopesPlaceholder: 'Select scopes...',
        cancel: 'Cancel',
        create: 'Create',
      },
      createdModal: {
        clientIdLabel: 'Client ID',
        clientSecretLabel: 'Client Secret',
        warning: 'This is the only time you can view the client secret. Save it in a secure location.',
        warningNote: 'Important!',
        ok: 'Ok',
      },
    },
    configWizardOverlay: {
      selectConfiguration: 'Select configuration',
      selectRevision: 'Select revision',
      node: 'Node',
      configuration: 'Configuration',
      configurations: 'Configurations',
      revision: 'Revision',
      revisions: 'Revisions',
      assign: 'Assign',
      cancel: 'Cancel',
      change: 'Change',
      next: 'Next',
      previous: 'Previous',
      reviewConfigurations: 'Review configuration assignment',
      reviewConfigurationJson: 'Review configuration JSON',
    },
    footer: {
      copyright: '© 2026 Mimic Networks, Inc.',
      contactUs: 'Need help? Contact us',
    },
    jobs: {
      listTitle: 'Jobs',
      end: 'End',
      jobDetailsTitle: 'Job',
      revision: 'Revision',
      start: 'Start',
      targetConfig: 'Target config',
      viewJob: 'View Job',
      jobDetailsTableTitle: 'Nodes',
      active: 'Active',
      inactive: 'Inactive',
      pauseBanners: 'Pause banner alerts',
      jobNodeIssuesModal: {
        noContentFound: 'No content found',
        warnings: 'Warnings',
        errors: 'Errors',
      },
      jobNodes: {
        hostName: 'Host name',
        nodeVersion: 'Node version ({{startDate}})',
        os: 'OS',
        status: 'Status',
        processing: 'Processing',
        configured: 'Configured',
        rejected: 'Node rejected the configuration:',
        warning: 'Configured with warnings:',
      },
      table: {
        jobName: 'Job name',
        subtenant: 'Subtenant',
        targetConfig: 'Target config',
        os: 'OS',
        timedout: 'Timed out',
        failed: 'Failed',
        warning: 'Warning',
        success: 'Success',
        started: 'Started',
        startedBy: 'By',
      },
      banner: {
        dismissJobBannerError: 'Error dismissing job banner',
        jobStatusError: 'Unable to display Jobs status',
        error: 'error',
        warning: 'warning',
        running: 'running',
        runningJob: 'running job',
        timeout: 'timeout',
        timedOutJob: 'timed out job',
        aggregated: '{{notificationType}}s have been aggregated and can be viewed individually on the Jobs page',
        viewJobs: 'View Jobs',
        pauseBannerAlerts: 'Pause all banner alerts',
        bannersPaused: 'Banner alerts paused',
      },
      pauseBannersModal: {
        title: 'Pause banner alerts',
        description:
          'Banner alerts will be paused for 24 hours. You can turn them back on by turning the toggle off on the Jobs page.',
        confirm: 'Pause',
        cancel: 'Cancel',
      },
    },
    configureNodesJobWizard: {
      configureNodes: 'Configure nodes',
      selectNodes: 'Select nodes',
      selectConfig: 'Select config',
      reviewJob: 'Review job',
      startJob: 'Start job',
      previous: 'Previous',
      nodes: 'Nodes',
      selectANode: 'Please select at least one node',
      selectARevision: 'Please select a revision of a config',
      nodesSelected: 'nodes selected',
      configName: 'Config name',
      assignedTo: 'Assigned to',
      lastRevised: 'Last revised (UTC)',
      nodesLower: 'nodes',
      rev: 'Rev',
      configuringNodesCreates: 'Configuring nodes creates a job',
      jobName: 'Job name',
      targetConfig: 'Target config',
      os: 'OS',
      tags: 'Tags',
      nodeVersion: 'Node version',
      operationalState: 'Operational state',
      hostName: 'Host name',
      jobNameRequired: 'Please enter a job name',
      unableToStartJob: 'Unexpected error starting job',
    },
    installer: {
      download_installer: 'Download Installer',
      installer: 'installer',
      done_button: 'Done',
      steps: {
        default: {
          step1_pre_action: 'Download the ',
          step1_post_action: '.',
          step2: 'Run the installer on servers you wish to protect with a Mimic Node.',
          step3:
            'Once the node is installed, Mimic will send telemetry containing information about the server and the node will be added to your account.',
        },
      },
    },
    pagination: {
      summary: 'Showing {{start}}-{{end}} of {{total}} results',
      selectedItems: '{{selected}} selected',
    },
    menu: {
      activity: 'Activity',
      dashcam: 'Dashcam',
      subtenants: 'Subtenants',
      apiTokens: 'API Tokens',
      installerTokens: 'Installer Tokens',
      oauthClients: 'OAuth Clients',
      m2mClients: 'M2M Clients',
      nodes: 'Nodes',
      telemetry: 'Telemetry',
      reports: 'Reports',
      driftReports: 'Drift Reports',
      changeAudit: 'Change Audit',
      biDashboards: 'BI Dashboards',
      configurations: 'Configurations',
      notifications: 'Notifications',
      feed: 'Feed',
      management: 'Management',
      releaseManagement: 'Release Management',
      mimicCli: 'Mimic CLI',
      download: 'Download',
      downloadNode: 'Download Node',
      extensibility: 'Extensibility',
      nodePlugins: 'Node',
      authenticate: 'Authenticate',
      users: 'Users',
      userActivity: 'User Activity',
      jobs: 'Jobs',
      tags: 'Tags',
      logout: 'Log out',
      docs: 'Docs',
      apiReference: 'API Reference',
      systemInfo: 'About this platform',
      linux: 'Linux',
      windows: 'Windows',
      contactSupport: 'Contact Support',
    },
    notifications: {
      title: 'Notifications',
      columns: {
        notification: 'Notification',
        urgency: 'Urgency',
        timestamp: 'Timestamp',
        channels: 'Channels',
      },
      clear_node_telemetry: {
        hallmarks: {
          subtitle: '{{user}} cleared hallmarks for',
        },
        deflections: {
          subtitle: '{{user}} cleared deflections for',
        },
        signals: {
          subtitle: '{{user}} cleared signals for',
        },
        reason: 'Reason: {{reason}}',
      },
    },
    notificationManagement: {
      title: 'Notification Management',
      subtitle: "Automatically send security alerts to your team's preferred tools",
      setupTitle: 'Set up a new connection',
      setupSubtitle: 'Select where you want to send notifications',
      selectDestination: 'Select a destination',
      addConnection: 'Add connection',
      cancel: 'Cancel',
      createSuccess: 'Alert configuration created successfully',
      createError: 'Failed to create alert configuration',
      unsupportedType: 'This destination type is not yet supported',
      newConnection: 'New Connection',
      table: {
        connectionName: 'Connection Name',
        destination: 'Destination',
        status: 'Status',
        created: 'Created',
      },
      steps: {
        setupConnection: 'Setup Connection',
        setParameters: 'Set Parameters',
        review: 'Review',
      },
      setupConnection: {
        title: 'Setup {{destination}} Connection',
        description: "You'll need to set up Mimic as an application in {{destination}}.",
        connectionName: 'Connection Name',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Optional',
        webhookUrl: 'Webhook URL',
        test: 'Test',
        token: 'Token',
        disableTls: 'Disable TLS verification',
        nextLabel: 'Select Notifications',
      },
      parameters: {
        title: 'Select Notification Parameters',
        subtitle:
          'Live notifications from your tenant will be sent to your {{destination}} instance. Set your parameters below.',
        eventTypesTitle: 'Event types',
        eventTypesDescription:
          'Raw occurrences or activities detected by the system that represent something happening on an endpoint or network',
        events: {
          signal: 'Signals',
          signalDescription:
            'Individual data points collected from endpoint sensors indicating potential security-relevant activity.',
          signalWarning: 'Signals can be very noisy',
          deflection: 'Deflections',
          deflectionDescription:
            'Blocked or mitigated threats where the system successfully prevented malicious activity.',
          hallmark: 'Hallmarks',
          hallmarkDescription:
            'High-confidence indicators of compromise that match known attack patterns or signatures.',
          lifecycle: 'Lifecycle',
          lifecycleDescription: 'Node status changes such as connections, disconnections, and configuration updates.',
        },
        nodesTitle: 'Nodes',
        nodesDescription: 'Select specific nodes to receive notifications for, or leave empty to include all nodes.',
        addNodes: 'Add Nodes',
        tagsTitle: 'Tags',
        tagsDescription: 'Filter alerts by node tags.',
        includeTitle: 'Included Key : Value Pairs',
        includeDescription: 'Only send notifications for events matching all of these key-value pairs.',
        excludeTitle: 'Excluded Key : Value Pairs',
        excludeDescription: 'Suppress notifications for events matching any of these key-value pairs.',
        keyPlaceholder: 'Key...',
        valuePlaceholder: 'Value...',
        add: 'Add',
        nextLabel: 'Review',
        previousLabel: 'Connect to {{destination}}',
      },
      selectNodes: {
        title: 'Select Nodes',
        description: 'Choose which nodes should trigger notifications for this connection.',
        hostName: 'Host Name',
        nodeId: 'Node ID',
        nodeTags: 'Node Tags',
        searchPlaceholder: 'Search nodes',
        addNodes: 'Add {{count}} node',
        addNodes_other: 'Add {{count}} nodes',
      },
      review: {
        title: 'Review Configuration',
        subtitle: 'Review your notification configuration before creating it.',
        destination: 'Destination',
        connectionName: 'Connection Name',
        description: 'Description',
        webhookUrl: 'Webhook URL',
        eventTypes: 'Event Types',
        nodes: 'Nodes',
        allNodes: 'All nodes (no filter)',
        tags: 'Tags',
        includeFilters: 'Included Key:Value Pairs',
        excludeFilters: 'Excluded Key:Value Pairs',
        none: 'None selected',
        submit: 'Create Alert',
        update: 'Save Changes',
        previousLabel: 'Set Parameters',
      },
      details: {
        back: 'Back',
        notFound: 'Alert configuration not found.',
        active: 'Active',
        suspended: 'Suspended',
        created: 'Created',
        edit: 'Edit',
        suspendConnection: 'Suspend Connection',
        activateConnection: 'Activate Connection',
        connection: 'Connection',
        eventTypes: 'Event Types',
        nodes: 'Nodes',
        allNodes: 'All nodes (no filter applied)',
        tags: 'Tags',
        includedKeyValuePairs: 'Included Key : Value Pairs',
        excludedKeyValuePairs: 'Excluded Key : Value Pairs',
        signalWarning: 'Signals can be very noisy',
        statusUpdated: 'Connection status updated',
        statusUpdateError: 'Failed to update connection status',
        configUpdated: 'Configuration updated',
        configUpdateError: 'Failed to update configuration',
        deleteConnection: 'Delete Connection',
        deleteSuccess: 'Connection deleted successfully',
        deleteError: 'Failed to delete connection',
        viewResponses: 'View Responses',
        overview: 'Overview',
        responses: 'Responses',
      },
      responses: {
        title: 'Alert Responses',
        subtitle: 'View alert delivery history and test your connection',
        backToDetails: 'Back to Alert Details',
        eventType: 'Event Type',
        status: 'Status',
        timestamp: 'Timestamp',
        testBadge: 'TEST',
        generateTest: 'Generate Test Event',
        waitingForResponse: 'Waiting for response...',
        testAlertQueued: 'Test alert queued successfully',
        testAlertReceived: 'Test alert response received',
        testAlertTimeout: 'Test alert response not available after 1 minute. Please check again later.',
        testAlertError: 'Failed to send test alert',
        statusCode: 'Status Code',
        error: 'Error',
        yes: 'Yes',
        no: 'No',
        errorMessage: 'Error Message',
        responseBody: 'Response Body',
        telemetryId: 'Telemetry ID',
      },
      confirmDelete: {
        title: 'Delete "{{name}}"?',
        body1: 'This will permanently delete this notification connection. All configuration will be lost.',
        body2: 'This action cannot be undone.',
        cancel: 'Cancel',
        ok: 'Delete',
      },
    },
    systemInfo: {
      title: 'About this platform',
      mgmtPlaneVersion: 'Management Plane version',
      dashboardVersion: 'Dashboard version',
    },
    tags: {
      title: 'Tags',
      subtitle: 'Manage your tenant tags.',
      newTag: 'New tag',
      tags: 'Tags',
      name: 'Name',
      useCount: 'Use Count',
      confirmDelete: {
        title: 'Are you sure you want to delete the tag “{{name}}”?',
        body1:
          'This tag is currently associated with {{useCount}} nodes. Deleting it will remove the tag from the nodes, and the tag will not be available anymore.',
        body2: 'This action cannot be reversed.',
        cancel: 'No, cancel',
        ok: 'Yes, delete',
      },
      actions: {
        deleteTag: 'Delete tag',
        editTagButton: 'Edit',
      },
      tagForm: {
        nameRequired: 'Name is required.',
        nameMaxLength: 'Tag name cannot be longer than {{maxLength}} characters.',
      },
      addTag: {
        namePlaceholder: 'Name new tag...',
        submitButton: 'Add',
      },
      editTag: {
        title: 'Edit tag name',
        namePlaceholder: 'Name tag...',
        cancelButton: 'Cancel',
        submitButton: 'Save',
      },
      newTagCreated: 'New tag "{{name}}" was created',
      newTagCreatedError: 'Error creating tag: {{name}}',
      tagUpdated: 'Tag name was updated to "{{newName}}" from "{{oldName}}"',
      tagUpdateError: 'Error updating tag name with: {{name}}',
      tagDeleted: 'The tag "{{name}}" was deleted and removed from {{useCount}} associated nodes.',
      tagDeleteError: 'Error deleting tag: {{name}}',
      addTagError: 'Error adding tag: {{name}}',
      removeTagError: 'Error removing tag: {{name}}',
      updateBaselineError: 'Error updating baseline',
      updateBaselineSuccess: 'Successfully updated baseline',
    },
    subtenants: {
      title: 'Subtenants',
      subtitle: 'Manage your tenant subtenants.',
      newSubtenant: 'New subtenant',
      subtenant: 'Subtenant',
      subtenants: 'Subtenants',
      parent: 'Parent',
      name: 'Name',
      confirmDelete: {
        title: 'Delete Subtenant',
        body1: 'Are you sure you want to delete the subtenant “{{name}}”?',
        cancel: 'Cancel',
        ok: 'Yes, Delete Subtenant',
        subtenantNotFound: '{{name}} not found',
        subtenantDeleteConflict: '{{name}} must be empty to delete',
        subtenantDeleteError: 'Unknown error deleting {{name}}',
      },
      actions: {
        deleteSubtenant: 'Delete Subtenant',
        editSubtenantButton: 'Edit Subtenant',
        users: 'Users',
        backToSubtenants: 'Back to Subtenants',
      },
      subtenantForm: {
        nameRequired: 'Name is required.',
        nameMaxLength: 'Subtenant name cannot be longer than {{maxLength}} characters.',
        descriptionMaxLength: 'Subtenant name cannot be longer than {{maxLength}} characters.',
        ok: 'Ok',
        cancel: 'Cancel',
      },
      addSubtenant: {
        namePlaceholder: 'Name new subtenant...',
        submitButton: 'Add',
        modal: 'New Subtenant',
        subtenantName: 'Subtenant Name',
        description: 'Description',
        badgePreview: 'Badge Preview',
        parentSubtenant: 'Parent Subtenant',
        parentSubtenantPlaceholder: 'Select a parent subtenant...',
        parentSubtenantLoading: 'Loading...',
        parentSubtenantNotFound: 'No subtenants found',
      },
      editSubtenant: {
        title: 'Edit subtenant name',
        namePlaceholder: 'Name subtenant...',
        cancelButton: 'Cancel',
        submitButton: 'Save',
      },
      deleteSubtenant: {
        disableDelete: 'Must be empty to delete',
      },
      newSubtenantCreated: 'New subtenant "{{name}}" was created',
      newSubtenantCreatedError: 'Error creating subtenant: {{name}}',
      subtenantUpdated: 'Subtenant name was updated to "{{newName}}" from "{{oldName}}"',
      subtenantUpdateError: 'Error updating subtenant name with: {{name}}',
      subtenantDeleted: 'The subtenant "{{name}}" was deleted.',
      subtenantLoadingError: 'Error loading subtenants',
      subtenantLoading: 'Loading subtenants...',
      noSubtenants: 'No subtenants found',
      errorFetchingSubtenant: 'There was an error fetching subtenant',
      errorFetchingSubtenants: 'There was an error fetching subtenants',
      searchForMore: 'Search for more...',
      description: 'Description',
      noDescriptionProvided: 'No description provided',
      parentSubtenant: 'Parent Subtenant',
      members: 'Members',
      user: 'User',
      email: 'Email',
      role: 'Role',
      roles: {
        admin: 'Admin',
        editor: 'Editor',
        viewer: 'Viewer',
        noAccess: 'No access',
        inherit: 'Inherit',
      },
      errors: {
        unableToUpdateMembership: 'Unable to update subtenant membership',
      },
    },
    users: {
      title: 'Users',
      subtitle: 'Manage subtenant access',
      newUser: 'New user',
      active: 'Active',
      removed: 'Removed',
      back: 'Back',
      name: 'Name',
      role: 'Role',
      dateAdded: 'Date added',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Company email address',
      invite: {
        title: 'Add user to tenant',
        firstNamePlaceholder: 'Enter first name',
        lastNamePlaceholder: 'Enter last name',
        emailPlaceholder: 'Enter email',
        subtenantRole: 'Default role',
        permissions: 'Permissions',
        permissionsPlaceholder: 'Select Permissions',
        roleMissingError: 'Select member permissions',
        enterValidEmail: 'Company email address',
        required: 'This is required',
        buttonTextUnconfirmed: 'Add',
        buttonTextPending: 'Adding...',
        buttonTextConfirmed: 'Added!',
      },
      roles: {
        superadmin: 'Super Admin',
        admin: 'Admin',
        editor: 'Editor',
        viewer: 'View Only',
        adminMessage: 'Full access with all permissions',
        editorMessage: 'Access with limited permissions',
        viewerMessage: 'Access with read-only permissions',
      },
      updateSubtenants: {
        assignSubtenantsAndRoles: 'Assign roles',
        assignRolesDescription: 'Update user access to subtenants',
        assign: 'Assign',
        name: 'Name',
        role: 'Role',
        noAccess: 'No access',
        inherit: 'Inherit',
        admin: 'Admin',
        editor: 'Editor',
        viewer: 'Viewer',
      },
      actions: {
        makeSuperadmin: 'Make a superadmin',
        makeAdmin: 'Make an admin',
        makeEditor: 'Make an editor',
        makeViewer: 'Make a viewer',
        makeSubtenantOnly: 'Make a subtenant only',
        makeTenantUser: 'Make a tenant user',
        remove: 'Remove this account',
        restore: 'Restore',
        update: 'Update member information',
        view: 'View',
        delete: 'Delete',
        assignToSubtenant: 'Assign to subtenant',
        editUser: 'Edit user',
        deleteUser: 'Delete user',
      },
      columns: {
        role: 'Default Permissions',
        dateCreated: 'Date Added',
        subtenantOnly: 'Subtenant Only',
        subtenantOnlyTooltip: "Subtenant only users don't have access to the main tenant resources",
      },
      feedback: {
        userInvited: '{{user}} was added as a member at {{email}}',
        userUpdated: '{{user}} updated',
        userAdded: 'Subtenant roles updated for {{user}}',
        errorOnInvite: 'Error inviting {{user}}',
        errorOnUpdate: 'Error updating {{user}}',
      },
      update: {
        title: 'Update member information',
        buttonTextUnconfirmed: 'Update',
        buttonTextPending: 'Updating...',
        buttonTextConfirmed: 'Updated!',
      },
      delete: {
        title: 'Delete User',
        description: 'Are you sure you want to delete {{userName}} from this tenant?',
        cancel: 'Cancel',
        confirm: 'Yes, Delete User',
      },
      superadminConfirm: {
        title: 'Make Superadmin',
        description:
          'Are you sure you want to make {{userName}} a superadmin? This will give them full access to all tenants and system settings.',
        cancel: 'Cancel',
        confirm: 'Yes, Make Superadmin',
      },
    },
    mimicSyntaxHighlighter: {
      copy: 'Copy',
      copied: 'Copied',
      failedCopy: 'Failed to copy to clipboard',
    },
    person: {
      name: 'Name',
      email: 'Email',
      password: 'Password',
    },
    logIn: {
      welcome: 'Welcome to Mimic',
      instructions: 'Enter your work email address to log in.',
      email: 'Enter email address...',
      continue: 'Continue',
      rememberMe: 'Remember my email',
      emailMismatch: 'There is an email mismatch with the identity provider',
      unexpectedError: 'Unexpected error',
    },
    nodes: {
      title: 'Nodes',
      failedToLoad: 'Node list failed to load',
      configured: 'Configured',
      pending: 'Pending',
      archived: 'Archived',
      configureNodes: 'Configure nodes',
      table: {
        header: {
          subtenant: 'Subtenant',
          dateCreated: 'Created (UTC)',
          host_name: 'Host Name',
          lastSeen: 'Last seen (UTC)',
          nickname: 'Nickname',
          tags: 'Tags',
          status: 'Status',
          connectivity: 'Connectivity (UTC)',
          operationalState: 'Operational State',
          vigilance: 'Vigilance',
          os: 'OS',
          configuration: 'Configuration',
          configurationUpdated: 'Updated by {{updatedBy}} on {{updatedAt}} (UTC)',
          customer: 'Customer',
          nodeVersion: 'Node Version',
          config: 'Config',
          mode: 'Mode',
          configurationStatus: 'Configuration Status',
        },
        maintenance: 'Maintenance',
        standard: 'Standard',
        audit: 'Audit',
        view_button: 'View',
        notAvailable: 'not available',
        assignConfiguration: 'Assign Configuration',
        configurationInProgress: 'Configuration in progress',
        build: 'Build {{buildNumber}}',
        synced: 'Synced',
        outOfSync: 'Out of Sync',
        windows: 'Windows',
        linux: 'Linux',
      },
      banner: {
        selected: 'selected',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        differentSubtenantWarning: 'Nodes belong to different subtenants',
        differentOSWarning: "Nodes have different OS's",
        noSystemProfileInfo: 'Some nodes have no system profile info',
        differentSubtenantError: 'Nodes must belong to the same subtenant to be configured together',
        differentOSError: 'Nodes must have the same OS to be configured together',
        configureNodes: 'Configure nodes',
        onlyDisconnectedCanBeArchived: 'Only disconnected nodes can be archived',
      },
      subtenant: {
        nodeChangedSubtenantSuccess: 'Node changed from "{{oldSubtenant}}" subtenant to "{{newSubtenant}}"',
        nodeChangedSubtenantFail: 'Failed to change node subtenant from "{{oldSubtenant}}" to "{{newSubtenant}}"',
      },
      actions: 'Actions',
      toggleState: 'Toggle State',
      toggleStateModal: {
        title: 'Pending Changes',
        hostname: 'Hostname',
        requestedState: 'Requested State',
      },
      toggleMaintenance: 'Toggle Maintenance',
      toggleMaintenanceModeModal: {
        title: 'Toggle Maintenance Mode',
        hostname: 'Hostname',
        currentMode: 'Current Mode',
        enterButton: 'Enter Maintenance',
        exitButton: 'Exit to Standard',
        cancel: 'Cancel',
        enterSuccess: 'Nodes are entering maintenance mode',
        exitSuccess: 'Nodes are exiting maintenance mode',
        enterError: 'Failed to enter maintenance mode',
        exitError: 'Failed to exit maintenance mode',
        mixedSelectionWarning:
          'Selected nodes have different modes. Select only standard or only maintenance nodes to proceed.',
      },
      createFleet: 'Create Fleet',
      createFleetWizard: {
        title: 'Create Fleet',
        steps: {
          reviewNodes: 'Review Selected Nodes',
          selectConfig: 'Select Configuration',
          selectRevision: 'Select Revision',
          details: 'Fleet Details',
          review: 'Review & Create',
        },
        reviewNodes: {
          description: '{{count}} nodes selected',
          inFleetError:
            '{{count}} node(s) are already in a fleet and cannot be added to a new fleet. Remove them to proceed.',
          inFleet: 'In Fleet',
          remove: 'Remove Selected',
          columns: {
            hostname: 'Hostname',
            operatingSystem: 'Operating System',
            connectivity: 'Connectivity',
            lastSeen: 'Last Seen',
          },
        },
        details: {
          name: 'Fleet Name',
          namePlaceholder: 'Enter fleet name',
          nameRequired: 'Fleet Name is required',
          nameMaxLength: 'Name must be at most {{max}} characters',
          description: 'Description',
          descriptionPlaceholder: 'Enter description (optional)',
          operatingSystem: 'Operating System',
          osWindows: 'Windows',
          osLinux: 'Linux',
        },
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
        createFleet: 'Create Fleet',
        feedback: {
          success: 'Fleet created successfully',
          error: 'Failed to create fleet',
        },
      },
      archiveSelected: 'Archive selected',
      archiveSelectedNodesModal: {
        title: 'Archive selected nodes?',
        description:
          '{{count}} selected nodes will move to the Archived tab and no longer appear in configured node operations.',
        andMore: 'and {{count}} more',
        cancel: 'Cancel',
        success: 'Nodes archived',
        errorFallback: 'Failed to archive nodes',
      },
    },
    configRevisions: {
      title: 'Revisions',
      table: {
        revision: 'Revision',
        author: 'Author',
        id: 'ID',
        createdAt: 'Created at (UTC)',
      },
    },
    nodeConfigurations: {
      title: 'Configurations',
      active: 'Active',
      archived: 'Archived',
      uploadConfig: 'Upload Config',
      table: {
        header: {
          name: 'Name',
          assigned: 'Assigned to',
          revision: 'Latest revision',
          tags: 'Tags',
          os: 'OS',
          lastRevised: 'Last revised (UTC)',
          id: 'ID',
        },
        nameNotAvailable: 'not available',
        nodeCount: '{{nodeCount}} nodes',
        outOfDate: '{{nodeCount}} node(s) out of date',
      },
      upload: {
        modal: {
          title: 'Upload Configuration',
          titleRevision: 'Upload Revision for {{configName}}',
          titleParentSelected: 'Upload Configuration Revision',
          cancel: 'Cancel',
          uploadConfiguration: 'Upload Configuration',
          uploadRevision: 'Upload Revision',
          configurationFileRequired: 'Configuration file is required',
          configurationUploadedSuccessfully: 'Configuration uploaded successfully',
          configurationRevisionUploadedSuccessfully: 'Configuration revision uploaded successfully',
          failedToUploadConfiguration: 'Failed to upload configuration',
          failedToUploadConfigurationRevision: 'Failed to upload configuration revision',
          errorBannerFailed: 'The file failed to upload because of the following error(s):',
          errorBannerCorrection: 'Please correct the error(s) and try again.',
        },
        form: {
          parentConfiguration: 'Parent Configuration (Optional)',
          parentConfigurationHelp: 'Select an existing configuration to inherit settings from',
          parentConfigurationPlaceholder: 'Search for existing configuration...',
          configurationFile: 'Configuration File',
          configurationFileUploadText: 'Click or drag JSON file to upload',
          configurationFileUploadSubtext: 'Maximum file size: 10MB',
          configurationSchemaVersion: 'Configuration Schema Version',
          configurationSchemaVersionHelp: 'Version identifier for this configuration (e.g., v1.0.1, v1.0.2)',
          configurationSchemaVersionPlaceholder: 'e.g., v1.0.1',
          configurationName: 'Configuration Name',
          configurationNamePlaceholder: 'Enter configuration name',
          operatingSystem: 'Operating System',
          operatingSystemPlaceholder: 'Select operating system',
          windows: 'Windows',
          linux: 'Linux',
          loading: 'Loading...',
          noConfigurationsFound: 'No configurations found',
          errors: {
            fileRequiredJson: 'File must be a JSON file',
            fileTooLarge: 'File must be smaller than 10MB',
            invalidJsonFile: 'Invalid JSON file',
            configurationFileRequired: 'Configuration file is required',
            configurationSchemaVersionRequired: 'Configuration schema version is required',
            configurationSchemaVersionTooLong: 'Schema version cannot exceed {{maxLength}} characters',
            configurationNameRequired: 'Configuration name is required',
            configurationNameTooLong: 'Name cannot exceed {{maxLength}} characters',
            operatingSystemRequired: 'Operating system is required',
          },
        },
      },
    },
    nodeConfigurationDetail: {
      title: 'Config',
      notes: 'Notes',
      updatingNotesFailed: 'Failed to update notes',
      notesPlaceholder: 'Notes go here',
      header: {
        uploadRevision: 'Upload Revision',
        archive: 'Archive',
        unarchive: 'Unarchive',
        disabledNodesAssigned: 'Can not archive when assigned to nodes',
        disabledDefaultConfig: 'Can not archive default config',
        errorOnArchive: 'Error archiving config',
        successOnArchive: 'Successfully archived config',
        successOnUnarchive: 'Successfully unarchived config',
        errorOnUnarchive: 'Error unarchiving config',
      },
      tabs: {
        revisions: 'Revisions',
        assignedNodes: 'Assigned nodes',
        jobsHistory: 'Jobs history',
      },
      assignedNodes: {
        title: 'Assigned nodes',
        assignConfigToNode: 'Assign config to node',
        table: {
          header: {
            nodes: 'Nodes',
            tags: 'Tags',
            configVersion: 'Config version',
            dateConfigRevisionApplied: 'Last updated (UTC)',
            assginedBy: 'Assigned by',
            hostName: 'Host name',
            nodeVersion: 'Node version',
            os: 'OS',
            connectivity: 'Connectivity',
            revision: 'Revision',
            configuredBy: 'Configured by',
            configured: 'Configured (UTC)',
          },
          processing: 'Processing',
        },
        noNodesAssigned: 'This config is not assigned to any nodes',
      },
      revisionHistory: {
        title: 'Revision history',
      },
      revisionsTable: {
        revisionDesription: 'Revision description',
        author: 'Author',
        created: 'Created (UTC)',
      },
      ConfigurationOverview: {
        title: 'Configuration overview',
        configurationId: 'Configuration ID',
        os: 'OS',
        latestRevision: 'Latest revision',
        lastUpdated: 'Last updated',
        windows: 'Windows',
        linux: 'Linux',
      },
    },
    node: {
      overview: 'Overview',
      serverInventory: 'Server Inventory',
      telemetry: 'Telemetry',
      autoRefresh: 'Auto-refresh',
      comingSoon: 'Coming Soon',
      comingSoonSubtitle: 'This feature is currently under development.',
      node_description: 'Description',
      node_description_placeholder: 'Node description goes here...',
      addTag: 'Add tag',
      server_configuration: 'Server configuration',
      active_directory: 'Active Directory',
      fqdn: 'FQDN',
      roles: 'Roles',
      yes: 'Yes',
      no: 'No',
      ip_address: 'IP Address',
      os: 'OS',
      windows_domain: 'Windows Domain',
      windows_workgroup: 'Windows Workgroup',
      cpuModelName: 'CPU Model Name',
      cpuArchitecture: 'CPU Architecture',
      osDescription: 'Description',
      osPath: 'OS Path',
      hostname: 'Hostname',
      lastUpdatedOn: 'Last updated on',
      nodeConfiguration: 'Node configuration',
      currentConfiguration: 'Current configuration',
      archivedConfigurationWarning: 'Archived',
      lastUpdatedBy: 'Last updated by',
      configuration: 'Configuration',
      revision: 'Revision',
      selectNewConfig: 'Select Configuration',
      selectNewRevision: 'Select Revision',
      notConfigured: 'This node is not configured',
      assignConfiguration: 'Assign configuration',
      configureNode: 'Configure Node',
      node_id: 'Node ID',
      node_version: 'Node version',
      node_config_id: 'Node Config ID',
      config_file: 'Config File',
      config_file_description: 'Config File Description',
      node_state: 'Node State',
      vigilance: 'Vigilance',
      tags: 'Tags',
      credentials: 'Credentials',
      registered: 'Registered',
      heartbeats: 'Heartbeats',
      last_configuration_heartbeat: 'Last configuration heartbeat',
      last_credentials_heartbeat: 'Last credentials heartbeat',
      unknown_heartbeat: 'Unknown',
      cert_expiration: 'Certificate expiration',
      last_cert_rotation: 'Last certificate rotation',
      last_seen: 'Last seen',
      node_status: 'Status',
      node_mode: 'Mode',
      hallmarks: 'Hallmarks',
      detections: 'Detections',
      encryptions: 'Encryptions',
      encryptions_present: 'Encryptions Present',
      deflections: 'Deflections',
      signals: 'Signals',
      connectivity: 'Connectivity',
      activity: 'Activity',
      operationalState: 'Node operational state',
      operationalStateLabel: 'Operational state',
      nodeConnectivity: 'Node connectivity',
      lifecycle_events: 'Node Lifecycle Events',
      events: 'Events',
      date: 'Date',
      active: 'Active',
      inactive: 'Inactive',
      idle: 'Idle',
      back: 'Back to Nodes',
      update_baseline: 'Update Baseline',
      clear_hallmarks: 'Clear Hallmarks',
      clear_selected: 'Clear selected',
      clear_all: 'Clear all',
      operationalStateChanging:
        'Processing your request to change status node operational state from {{currentState}} to {{requestedState}}',
      operationalStateChangeRequested: 'Operational state change requested',
      operationalStateChangeFailed: 'Operational state failed to update',
      operationalStateChangeFailedDescription:
        'The vigilance level of this node failed to update and is still set to {{requestedState}}. Please try again. If the issue persists, contact your system administrator.',
      configAppliedChangeFailed: 'Configuration failed to update',
      configAppliedChangeFailedDescription:
        'The configuration of this node failed to change and has remained as {{configName}}. Please try again. If this issue persists, contact your system administrator.',
      unknown: 'Unknown',
      maintenanceMode: 'Maintenance Mode',
      maintenanceModeOff: 'Off',
      maintenanceModeOn: 'Maintenance',
      enterMaintenance: 'Maintenance Mode',
      endMaintenance: 'End Maintenance Mode',
      nodeInMaintenanceMode: 'Node is in maintenance mode',
      previousConfiguration: 'Previous configuration',
      enterMaintenanceModal: {
        title: 'Maintenance Mode',
        description: "Changes in your server won't be blocked during this maintenance window",
        start: 'Start Maintenance Mode',
        cancel: 'Cancel',
      },
      exitMaintenanceModal: {
        title: 'Exit Maintenance Mode',
        description:
          'This node is leaving Maintenance Mode. Going into Audit Mode next will reduce your false positives while Mimic re-learns the new state of your system after the changes that took place. In Audit Mode, Mimic will not deflect any actions, but will report all unauthorized changes as signals, that can then be used to update your protection configuration before going back into Active Mode.',
        durationLabel: 'Auto-exit duration (hours):',
        durationHelp: 'Audit mode will automatically end after this duration.',
        skipDescription: 'If you would like to skip, the previous protections will be applied as configured.',
        skipAudit: 'Skip Audit',
        continue: 'Continue to Audit Mode',
      },
      nodeInAuditMode: 'Node is in Audit Mode',
      endAuditMode: 'End Audit Mode',
      auditModeEntered: 'Audit mode entered successfully',
      auditModeScheduledExit: 'Auto-exit in',
      auditModeScheduleWarning:
        'Audit mode entered, but automatic exit could not be scheduled. You will need to manually exit audit mode.',
      auditModeEntryFailed: 'Failed to enter audit mode',
      auditModeError: {
        missingPreviousConfig: 'Cannot enter audit mode: previous configuration not found',
        jobTimeout: 'Audit mode entry timed out. Please try again.',
      },
      maintenanceModeChangeRequested: 'Maintenance mode change requested',
      maintenanceModeChangeFailed:
        'There was an issue applying maintenance mode. Please try again later or contact your support team if the problem persists.',
      maintenanceModeExitFailed:
        'There was an issue exiting maintenance mode. Please try again later or contact your support team if the problem persists.',
      maintenanceModeChanging: 'Processing your request to change maintenance mode...',
      maintenanceModeUnsupported: 'Maintenance mode is not supported on this node because the node version is unknown',
      maintenanceModeUnsupportedTooltip:
        'Maintenance Mode is not supported for this Node, please upgrade to the latest Windows or Linux version',
      deflectionsActive: 'Active',
      deflectionsInactive: 'Inactive',
      noSystemProfileInfo: 'Unable to configure. No system profile info',
      archive_node: 'Archive Node',
      archived: 'Archived',
      archiveNodeSuccess: 'Node archived successfully',
      archiveNodeError: 'Error archiving node',
      archiveNodeDisabled: 'This node is already archived',
      archiveNodeMustBeDisconnected: 'Node must be disconnected to archive',
      nodeArchivedBanner: '{{hostName}} has been archived - {{dateArchived}}',
      feedback: {
        operationalStateChanged: 'Node {{nodeId}} state is changing to {{state}}',
        errorFetchingConfigRevision: 'Error fetching config revision',
        assignNodeConfigError: 'Failed to assign config revision to node',
        nodeUpdated: 'Node updated',
        updateFailed: 'Failed to update node',
      },
      connectivityState: {
        connected: 'Connected',
        disconnected: 'Disconnected',
        degraded: 'Degraded',
        unknown: 'Unknown',
      },
      connectivityStateDesc: {
        connected: 'Node connected',
        disconnected: 'Node disconnected',
        degraded: 'Node degraded',
        unknown: 'Unknown',
      },
      hallmarksTable: {
        mimicID: 'Hallmark ID',
        mimicDescription: 'Hallmark',
        occurrences: 'Occurrences',
        firstSeen: 'First Seen (UTC)',
        lastSeen: 'Last Seen (UTC)',
      },
      telemetryEventsTable: {
        mimicID: 'Mimic ID',
        mimicDescription: 'Description',
        occurrences: 'Occurrences',
        firstSeen: 'First Seen (UTC)',
        lastSeen: 'Last Seen (UTC)',
        eventID: 'Event ID',
        eventTime: 'Event Time (UTC)',
      },
      telemetryEventDetails: {
        title: {
          hallmarks: 'Hallmark Details',
          deflections: 'Deflection Details',
          signals: 'Signal Details',
        },
      },
      tagDropdown: {
        typeToAddTag: 'To add a new tag, start typing',
        addTagOption: 'Add a new tag "{{tagName}}"',
        showMore: 'Show {{count}} more...',
      },
      hostNameNotAvailable: 'not available',
      selectConfiguration: 'Select a configuration',
      selectRevision: 'Select a revision',
      viewDetails: 'View details',
      selectConfigRevisionConfirmation: {
        title: 'Config Revision',
        description: 'Are you sure you want to change the config revision for this node?',
        yes: 'Yes',
        no: 'No',
      },
      status: {
        configuringTo: 'Configuring to',
        failedToConfigure: 'Failed to configure to',
        of: 'of',
        revision: 'revision',
      },
      eventsTable: {
        appliedConfig: 'Successfully configured to',
        rejectedConfig: 'Failed to configure to',
        pendingConfig: 'Pending configuration for',
        obtainedConfig: 'Changing configuration to',
      },
      clearTelemetryModal: {
        hallmarks: {
          title: 'Clear Hallmarks',
          reasonPlaceholder: 'Enter reason for clearing hallmarks...',
          buttonTextUnconfirmed: 'Clear Hallmarks',
        },
        deflections: {
          title: 'Clear Deflections',
          reasonPlaceholder: 'Enter reason for clearing deflections...',
          buttonTextUnconfirmed: 'Clear Deflections',
        },
        signals: {
          title: 'Clear Signals',
          reasonPlaceholder: 'Enter reason for clearing signals...',
          buttonTextUnconfirmed: 'Clear Signals',
        },
        error: 'Error clearing telemetry',
        reason: 'Reason',
        enterValidReason: 'Please enter a valid reason',
        enterValidReasonLength: 'Please enter a reason with at least {{min}} characters',
        buttonTextPending: 'Clearing...',
        buttonTextConfirmed: 'Cleared!',
      },
    },
    activity: {
      title: 'Activity',
      sections: {
        hallmarks: 'Hallmarks',
        deflections: 'Deflections',
      },
      noWorries: {
        hallmarks: 'No nodes with hallmarks',
        deflections: 'No nodes with deflections',
        signals: 'No nodes with signals',
      },
      allClear: 'Everything is clear.',
      unexpectedError: 'Unexpected Error',
      unexpectedErrorDescription: 'Sorry, something went wrong.',
      lastUpdated: 'Last updated',
      loading: 'Loading…',
      loadingMore: 'Loading more…',
      failedToLoad: 'Failed to load activity',
      emptyWindow: 'No {{section}} in the selected window.',
      actions: {
        refresh: '↻ Refresh',
      },
      table: {
        hostName: 'Host Name',
        occurrences: 'Occurrences',
        connectivity: 'Connectivity',
        firstSeen: 'First Seen (UTC)',
        lastSeen: 'Last Seen (UTC)',
      },
      window: {
        '1h': 'Last 1 hour',
        '3h': 'Last 3 hours',
        '12h': 'Last 12 hours',
        '24h': 'Last 24 hours',
        '7d': 'Last 7 days',
        '14d': 'Last 14 days',
        '30d': 'Last 30 days',
      },
      notAvailable: 'Not available',
    },
    telemetry: {
      title: 'Telemetry',
      events: 'Events',
      noEvents: 'No telemetry events found',
      allClear: 'Everything is clear.',
      notAvailable: 'Not available',
      startTime: 'Start time (UTC)',
      endTime: 'End time (UTC)',
      dateRangeExceeded: 'Date range cannot exceed 7 days',
      table: {
        eventType: 'Event Type',
        hostName: 'Host Name',
        mimicId: 'Mimic ID',
        description: 'Description',
        processPath: 'Process Path',
        resourcePath: 'Resource Path',
        configuration: 'Configuration',
        aclRule: 'ACL Rule',
        startTime: 'Start Time (UTC)',
      },
      eventDetails: {
        title: 'Telemetry Event Details',
        eventInfo: 'Event Info',
        timestamp: 'Timestamp (UTC)',
        node: 'Node',
        eventType: 'Event Type',
        mimicId: 'Mimic ID',
        description: 'Description',
        processPath: 'Process Path',
        resourcePath: 'Resource Path',
        appliedConfig: 'Applied Config',
        eventPayload: 'Event Payload',
        noPayload: 'No detailed event data available',
        notAvailable: 'N/A',
        configName: 'Config Name',
        configId: 'Config ID',
        revision: 'Revision',
        createdBy: 'Created By',
      },
    },
    nodeTelemetry: {
      groupedTable: {
        eventType: 'Event Type',
        events: 'Events',
        firstSeen: 'First Seen',
        lastSeen: 'Last Seen',
        truncated: 'Showing first 100 event types. Refine the date range to narrow results.',
      },
      streamTable: {
        time: 'Time',
        eventType: 'Event Type',
        summary: 'Summary',
        loadMore: 'Load more',
        loadingMore: 'Loading…',
        openEventDetails: 'Open event details for {{eventType}} at {{time}}',
      },
      filterBar: {
        preset: {
          last1Hour: 'Last 1 hour',
          last3Hours: 'Last 3 hours',
          last12Hours: 'Last 12 hours',
          last24Hours: 'Last 24 hours',
          last7Days: 'Last 7 days',
          last14Days: 'Last 14 days',
          last30Days: 'Last 30 days',
        },
        eventTypePlaceholder: 'Event type',
        searchEventTypes: 'Search event types...',
        noEventTypes: 'None found',
        reset: 'Reset',
      },
      eventsCard: {
        eventsHeader: 'Events',
        groupBy: 'Group by:',
        eventTypeChip: 'Event Type',
        viewMode: {
          stream: 'Stream',
          grouped: 'Grouped',
        },
      },
      eventDetails: {
        title: 'Event Details',
        close: 'Close event details',
        loading: 'Loading...',
        error: 'Failed to load event details',
        eventInfo: {
          title: 'Event Info',
          timestamp: 'Timestamp',
          node: 'Node',
          eventType: 'Event Type',
          eventId: 'Event ID',
        },
        appliedConfig: {
          title: 'Applied Config',
          configName: 'Config Name',
          revision: 'Revision',
          description: 'Description',
          createdBy: 'Created By',
        },
        processAncestry: {
          title: 'Process Ancestry',
          actor: 'ACTOR',
          target: 'TARGET',
          empty: 'No process ancestry data',
        },
        payload: {
          title: 'Payload',
        },
      },
      error: {
        title: 'Unable to load telemetry',
        description: 'Telemetry data could not be loaded.',
      },
    },
    cliSetup: {
      skipSetup: 'Skip Setup',
      title: 'Mimic Command Line Interface (CLI) Setup',
      failedCopy: 'Failed to copy text',
      failedToGenerate: 'Failed to generate token',
      copy: 'Copy',
      copied: 'Copied',
      auth: {
        title: 'CLI Authentication with Mimic',
        downloadLinkText: 'Go here to download the CLI.',
        generate: {
          button: 'Generate authentication token',
        },
        step1: {
          number: '1.',
          name: 'Check that Mimic is installed',
          instructions: 'In the terminal, enter',
          command: 'mimic version',
          successPart1: 'If a version number is returned, e.g.',
          successExample: 'Mimic 1.0.0',
          successPart2: ', go to step 2 to authenticate.',
          failurePart1: 'If you see anything else, e.g.',
          failureExample: 'mimic: command not found',
          failurePart2: ', you need to install the CLI.',
        },
        step2: {
          number: '2.',
          name: 'Authenticate with Mimic',
          instructions: 'In the terminal, enter',
          command: 'mimic login',
          description:
            'You will be asked for your email (your Mimic login) and an authentication token, which you’ll generate with the button below.',

          example: {
            loginCommand: '$ mimic login\nEnter email: {{email}}\nEnter the authentication token: {{token}}',
          },
        },
        help: {
          description: 'To view all the commands available for the Mimic CLI, type',
          command: 'mimic -h',
        },
      },
      download: {
        title: 'Download the Mimic CLI',
        subtitle:
          'Select an option below to download and install the appropriate CLI binary for your operating system.',
        start: 'Started download for the {{osType}} {{archType}} CLI.',
        error: 'Error downloading for the {{osType}} {{archType}} CLI.',
        after: {
          span1: 'After installing,',
          linkText: 'go here to authenticate with Mimic',
        },
        linux: 'Linux',
        windows: 'Windows',
        darwin: 'Mac',
        mac: 'Mac',
        macos: 'Mac',
        arm64: '(Apple Silicon)',
        x86_64: '(Intel)',
      },
      step0: {
        title: 'Welcome to Mimic!',
        body: 'Before you continue, we suggest setting up your Mimic Command Line Interface (CLI). This will help you to perform common platform tasks from the command line.',
        start: 'Start Mimic CLI Setup',
      },
      step1: {
        stepNumber: 'Step 1.',
        stepName: 'Download the CLI',
        body: 'Select an option below to download and install the appropriate CLI binary for your operating system.',
        downloadLinux: 'Download for Linux',
        downloadWindows: 'Download for Windows',
      },
      step2: {
        stepNumber: 'Step 2.',
        stepName: 'Verify installation',
        body: 'In your terminal, verify that the Mimic CLI has been installed by entering',
        command: 'Lorem ipsum',
        commandCopied: 'Command copied to clipboard',
        body2: 'Continue to the next step when you see',
        commandSuccess: 'Lorem ipsum',
      },
      step3: {
        stepNumber: 'Step 3.',
        stepName: 'Authenticate with token',
        body: 'Generate and enter the token below to authenticate with Mimic.',
        tokenCopied: 'Token copied to clipboard',
      },
      step4: {
        title: 'Congrats, you’ve successfully set up the Mimic CLI!',
        body: 'You can access these instructions again at',
        settingsCliSetup: 'Settings > Mimic CLI',
        continue: 'Continue to Mimic',
      },
    },
    apiReference: {
      requiredPermission: 'Required permission: {{permission}}',
    },
    error: {
      errorMesage: 'An error has occurred',
      badRequestError: 'Bad request',
      badRequestErrorDescription: 'The request was invalid or cannot be processed',
      forbiddenError: 'Forbidden',
      forbiddenErrorDescriptionOne: 'You do not have permission to view this page.',
      forbiddenErrorDescriptionTwo: 'Contact your administrator for access.',
      unexpectedError: 'Unexpected error',
      unexpectedErrorDescription: 'Sorry, something went wrong',
      notFoundError: 'Page not found',
      notFoundErrorDescription: 'Sorry, this page does not exist',
      notFoundContentError: 'Content not found',
      notFoundContentErrorDescription: 'Sorry, this is not available',
      conflictError: 'One or more of parts of your request caused a conflict',
      conflictErrorDescription: 'Please fix your request and try submitting again',
      goBack: 'Go back',
    },
    tenants: {
      errorFetchingTenants: 'There was an error fetching tenants',
      errorFetchingTenant: 'There was an error fetching tenant',
    },
    downloadNode: {
      download: 'Download {{version}}',
      pleaseWait: 'Please, wait',
      noReleases: 'No releases are currently available for this platform.',
      buildType: 'Build Type',
      buildTypeRelease: 'Release',
      buildTypeDebug: 'Debug',
      version: 'Version',
      flavor: 'Flavor',
      architecture: 'Architecture',
      platform: 'Platform',
      package: 'Package',
      fileSize: 'File Size',
      hash: 'Hash',
      releaseDate: 'Release Date',
      windows: {
        caption: 'Download Windows Node Installer',
        title: 'Mimic Ransomware Defense for Windows',
        description: 'Deploy the Mimic Node in your Windows server with the Node Installer.',
        viewOtherVersions: 'View other Windows node versions',
      },
      linux: {
        caption: 'Download Linux Node Installer',
        title: 'Mimic Ransomware Defense for Linux',
        description: 'Deploy the Mimic Node in your Linux server with the Node installer',
        viewOtherVersions: 'View other Linux node versions',
      },
      emptyState: {
        title: 'No Releases Available',
        superadminSubtitle:
          'No releases have been published for this tenant yet. Go to Release Management to publish releases.',
        userSubtitle: 'No releases have been published for this tenant yet. Please contact your administrator.',
        goToReleaseManagement: 'Go to Release Management',
      },
      systemRequirements: {
        title: 'System requirements',
        operatingSystem: {
          label: 'Operating system:',
          value: 'Windows Server 2016+',
        },
        designedFor: {
          label: 'Designed for:',
          value: 'x64 Windows system setups',
        },
        adminAccess: {
          label: 'Administrative access:',
          value: 'Local admin privileges on the Windows server',
        },
        diskSpace: {
          label: 'Available Disk Space:',
          value: 'Minimum 1GB of disk space',
        },
        memory: {
          label: 'Memory:',
          value: 'Based on vendor specs, the resource footprint is 240-350MB of memory',
        },
        cpu: {
          label: 'CPU:',
          value: '1-2% of one CPU core',
        },
        network: {
          label: 'Network:',
          value: 'Internet access to specific Mimic endpoints. HTTPS (port 443) connectivity',
        },
      },
      installInstructions: {
        title: 'Install instructions',
        step1: {
          prefix: 'After downloading the installer, generate an',
          linkText: 'installer token',
          suffix: '.',
        },
        msi: {
          step2: {
            text: 'Next, run the following command to use the MSI:',
            command: 'msiexec.exe /i mimic-installer-x64.msi /l*V mimic-install.log TOKEN=<your-installer-token>',
          },
        },
        exe: {
          step2: {
            text: 'Next, run the following command to use the EXE:',
            command: 'mimic-installer-x64.exe /verysilent /suppressmsgboxes /token=<your-installer-token>',
            graphicalOption: 'Or for a graphical installation:',
            graphical: 'Double click on the installer .exe and follow the instructions.',
            msiNote: 'Note: This MSI download only supports headless installation',
            graphicalAlternative: 'For a graphical installation, please use the .exe download',
          },
        },
        step3: {
          prefix: 'After completing installation, navigate to the',
          linkText: 'Nodes page',
          suffix: 'to configure the node.',
        },
      },
    },
    configPrettyViewer: {
      configurationOverview: 'Configuration Overview',
      noConfigurationData: 'No configuration data available',
      unableToParseStructure: 'Unable to parse configuration structure',
      unrecognizableContent: "This configuration doesn't contain recognizable ACL protection rules or engines",
      rawContentPreview: 'Raw content preview: {{preview}}...',
      prettyView: 'Pretty View',
      rawCode: 'JSON View',
      aclProtection: {
        title: 'ACL Protection',
        enabled: 'Enabled',
        disabled: 'Disabled',
        rulesGrouped: '{{count}} Rules grouped by {{actorCount}} Actors',
        anyActor: 'Any Actor',
        rulesCount: '{{count}} rules',
      },
      engines: {
        title: 'Engines',
        configured: '{{count}} Configured',
      },
      general: {
        title: 'General Configuration',
      },
    },
    grantOAuth: {
      title: 'Authorize Application',
      description: 'would like to access your Mimic account.',
      permissionsTitle: 'This application is asking for the following scopes:',
      grantButton: 'Grant Access',
      disclaimer:
        "By granting access, you allow this application to consume Mimic's API on your behalf with the scopes listed above.",
      invalidRequest: 'Invalid OAuth request. Missing required parameters.',
    },
    artifactsBrowser: {
      title: 'Artifacts',
      subtitle: 'Browse and download build artifacts from cloud storage',
      columns: {
        name: 'Name',
        size: 'Size',
        lastModified: 'Last Modified',
        actions: 'Actions',
      },
      download: 'Download',
      loadArtifacts: 'Load Artifacts',
      loadMore: 'Load More',
      refresh: 'Refresh',
      cloudArtifactsBeta: 'Cloud Artifacts Beta',
      root: 'node-artifacts',
      folder: 'Folder',
      file: 'File',
      errors: {
        loadFailed: 'Failed to load artifacts',
        downloadFailed: 'Failed to download artifact',
        channelNotAvailable: 'WebSocket channel not available',
      },
    },
    noNodes: {
      title: 'No Nodes Installed',
      subtitle: 'Get started by downloading and installing a Mimic Node.',
      downloadNode: 'Download Node',
    },
    releaseManagement: {
      listTitle: 'Release Management',
      description: 'Manage node releases available to this tenant',
      table: {
        os: 'OS',
        version: 'Version',
        schemaVersion: 'Schema',
        arch: 'Arch',
        dateReleased: 'Released',
        actions: 'Actions',
      },
      filters: {
        latestBuild: 'Latest Only',
      },
      publish: 'Publish',
      unpublish: 'Unpublish',
      publishSuccess: 'Release published successfully',
      unpublishSuccess: 'Release unpublished successfully',
      publishError: 'Failed to publish release',
      unpublishError: 'Failed to unpublish release',
      copyReleaseId: 'Copy release ID',
      copiedReleaseId: 'Release ID copied to clipboard',
    },
    userActivity: {
      title: 'User Activity',
      columns: {
        actor: 'Actor',
        type: 'Type',
        method: 'Method',
        endpoint: 'Endpoint',
        status: 'Status',
        duration: 'Duration (ms)',
        timestamp: 'Timestamp',
      },
      requestParams: 'Request Parameters',
    },
    fleets: {
      title: 'Fleets',
      backToFleets: 'Back to Fleets',
      fleetDetails: 'Fleet Details',
      newFleet: 'New Fleet',
      editFleet: 'Edit Fleet',
      editFleetDetails: 'Edit Fleet Details',
      deleteFleet: 'Delete Fleet',
      addNodes: 'Add Nodes',
      removeNodes: 'Remove Nodes',
      removeNodesCount: 'Remove {{count}} Node',
      removeNodesCountPlural: 'Remove {{count}} Nodes',
      save: 'Save',
      cancel: 'Cancel',
      actions: 'Actions',
      applyConfiguration: 'Apply current config',
      updateConfiguration: 'Update fleet config',
      toggleNodeState: 'Toggle node state',
      toggleNodeMode: 'Toggle node mode',
      name: 'Name',
      os: 'OS',
      fleetId: 'Fleet ID',
      operatingSystem: 'Operating System',
      windows: 'Windows',
      linux: 'Linux',
      macos: 'macOS',
      configuration: 'Configuration',
      nodeCount: 'Node Count',
      configurationStatus: 'Configuration Status',
      synced: 'Synced',
      outOfSync: 'Out of Sync',
      createdUtc: 'Created (UTC)',
      description: 'Description',
      noDescriptionProvided: 'No description provided',
      nodes: 'Nodes',
      form: {
        fleetName: 'Fleet Name',
        fleetNamePlaceholder: 'Enter fleet name',
        description: 'Description',
      },
      feedback: {
        fleetUpdatedSuccess: 'Fleet updated successfully',
        fleetUpdatedError: 'Failed to update fleet',
        nodesAddedSuccess: 'Nodes added successfully',
        nodesAddedError: 'Failed to add nodes',
        nodesRemovedSuccess: '{{count}} node removed successfully',
        nodesRemovedSuccessPlural: '{{count}} nodes removed successfully',
        nodesRemovedError: 'Failed to remove nodes',
        fleetDeletedSuccess: 'Fleet deleted successfully',
        fleetDeletedError: 'Failed to delete fleet',
        configAppliedSuccess: 'Configuration applied to all nodes',
        configAppliedError: 'Failed to apply configuration',
      },
      deleteModal: {
        title: 'Delete this fleet?',
        delete: 'Delete',
        body: 'Fleet deletion is non-destructive. All nodes will persist independently after the fleet is removed.',
      },
      addNodesDrawer: {
        title: 'Add Nodes to Fleet',
        selectNodesWindows: 'Select nodes to add to this fleet. Only Windows nodes not in a fleet are shown.',
        selectNodesLinux: 'Select nodes to add to this fleet. Only Linux nodes not in a fleet are shown.',
        addNodesCount: 'Add {{count}} Nodes',
        addNodes: 'Add Nodes',
      },
      updateConfigDrawer: {
        fleet: 'Fleet',
        steps: {
          selectConfig: 'Select Configuration',
          selectRevision: 'Select Revision',
          review: 'Review Changes',
        },
        updateConfig: 'Update Config',
        review: {
          title: 'Review Configuration Update',
          fleet: 'Fleet',
          nodesAffected: 'Nodes Affected',
          currentConfig: 'Current Configuration',
          newConfig: 'New Configuration',
        },
        feedback: {
          success: 'Fleet configuration updated successfully',
          error: 'Failed to update fleet configuration',
        },
      },
      tabs: {
        configured: 'Configured',
        fleets: 'Fleets',
        pending: 'Pending',
        archived: 'Archived',
      },
      wizard: {
        steps: {
          details: 'New Fleet',
          selectConfig: 'Select Configuration',
          selectRevision: 'Select Revision',
          selectNodes: 'Select Nodes',
          review: 'Review',
        },
        previous: 'Previous',
        next: 'Next',
        createFleet: 'Create Fleet',
        form: {
          name: 'Name',
          nameRequired: 'Name is required',
          nameMaxLength: 'Name must be at most {{max}} characters',
          namePlaceholder: 'Enter fleet name',
          description: 'Description',
          operatingSystem: 'Operating System',
          operatingSystemRequired: 'Operating system is required',
          operatingSystemPlaceholder: 'Select operating system',
        },
        review: {
          fleetDetails: 'Fleet Details',
          name: 'Name',
          description: 'Description',
          operatingSystem: 'Operating System',
          configuration: 'Configuration',
          revision: 'Revision',
          configId: 'Config ID',
          selectedNodes: 'Selected Nodes ({{count}})',
          noNodesSelected: 'No nodes selected',
        },
        feedback: {
          fleetCreatedSuccess: 'Fleet created successfully',
          fleetCreatedError: 'Failed to create fleet',
        },
      },
    },
  },
};
